/**
 * Audit slice (PX-W6.2) — the org audit log with cursor pagination.
 *
 * Filter changes RESET the list (the canonical filter string is the cache's
 * loaded key, so a changed key forces a fresh first page); "load more" APPENDS
 * the next page and records the new cursor — never a replace (INV-7).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as auditHttp from './audit.http'
import type { AuditEntry, AuditFilters } from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** The canonical filter string — the cache key's filter component. */
export const canonicalFilterKey = (filters: AuditFilters): string =>
  `${filters.action ?? ''}|${filters.entityType ?? ''}|${filters.actorUserId ?? ''}`

const cacheKey = (orgId: string | null, filters: AuditFilters): string =>
  `${orgId ?? ''}|${canonicalFilterKey(filters)}`

/** A first page: rows + the cursor the next page continues from. */
interface AuditFirstPage {
  key: string
  rows: AuditEntry[]
  nextCursor: string | null
}

export const fetchAuditPage = createAsyncThunk<AuditFirstPage, AuditFilters, {
  rejectValue: ApiError
}>(
  'audit/fetchAuditPage',
  async (filters, { getState, rejectWithValue }) => {
    try {
      const page = await auditHttp.auditHttp.list(filters)
      return {
        key: cacheKey(orgKey(getState), filters),
        rows: page.items ?? [],
        nextCursor: page.nextCursor ?? null
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (filters, { getState }) =>
      shouldFetch(
        (getState() as { audit: AuditState }).audit.cache,
        cacheKey(orgKey(getState), filters)
      )
  }
)

/** The next page under the CURRENT filters (appends). */
export const fetchMoreAudit = createAsyncThunk<AuditFirstPage, void, { rejectValue: ApiError }>(
  'audit/fetchMoreAudit',
  async (_, { getState, rejectWithValue }) => {
    const { audit } = getState() as { audit: AuditState }
    try {
      const page = await auditHttp.auditHttp.list(audit.filters, audit.nextCursor ?? undefined)
      return {
        key: audit.cache.loadedKey ?? '',
        rows: page.items ?? [],
        nextCursor: page.nextCursor ?? null
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const exportAudit = createAsyncThunk<Blob, void, { rejectValue: ApiError }>(
  'audit/exportAudit',
  async (_, { getState, rejectWithValue }) => {
    const { audit } = getState() as { audit: AuditState }
    try {
      return await auditHttp.auditHttp.export(audit.filters)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export interface AuditState {
  items: AuditEntry[]
  nextCursor: string | null
  filters: AuditFilters
  cache: FetchCache
  error: ApiError | null
  loadingMore: boolean
  exporting: boolean
  exportError: ApiError | null
}

const initialState: AuditState = {
  items: [],
  nextCursor: null,
  filters: {},
  cache: initialFetchCache,
  error: null,
  loadingMore: false,
  exporting: false,
  exportError: null
}

export const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    /** A filter edit in the UI (the Apply button dispatches the fetch). */
    draftFiltersChanged(state, action: PayloadAction<AuditFilters>) {
      state.filters = action.payload
      invalidate(state.cache)
    },
    retryClicked(state) {
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditPage.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchAuditPage.fulfilled, (state, action: PayloadAction<AuditFirstPage>) => {
        fetchSucceeded(state.cache, action.payload.key)
        state.items = action.payload.rows
        state.nextCursor = action.payload.nextCursor
      })
      .addCase(fetchAuditPage.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(fetchMoreAudit.pending, (state) => {
        state.loadingMore = true
      })
      .addCase(fetchMoreAudit.fulfilled, (state, action: PayloadAction<AuditFirstPage>) => {
        state.loadingMore = false
        // Append, deduping by id — the cursor window can overlap on writes.
        const seen = new Set(state.items.map((row) => row.id ?? ''))
        for (const row of action.payload.rows) {
          const id = row.id ?? ''
          if (id !== '' && seen.has(id)) continue
          seen.add(id)
          state.items.push(row)
        }
        state.nextCursor = action.payload.nextCursor
      })
      .addCase(fetchMoreAudit.rejected, (state, action) => {
        state.loadingMore = false
        state.error = action.payload ?? null
      })
      .addCase(exportAudit.pending, (state) => {
        state.exporting = true
        state.exportError = null
      })
      .addCase(exportAudit.fulfilled, (state) => {
        state.exporting = false
      })
      .addCase(exportAudit.rejected, (state, action) => {
        state.exporting = false
        state.exportError = action.payload ?? null
      })
  }
})

export const { draftFiltersChanged, retryClicked: auditRetryClicked } = auditSlice.actions
